import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { type ColumnDef } from '@tanstack/react-table';
import { Factory, Grid3x3, ScanBarcode } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataTable, arrIncludesSome, type FacetedFilter } from '@/components/ui/DataTable';
import { MetricCard } from '@/components/financials/MetricCard';
import { ProductionFormDialog } from '@/components/manufacturing/ProductionFormDialog';
import { ProductionFormSheet } from '@/components/manufacturing/ProductionFormSheet';
import { useProductionForms, type ProductionFormRow } from '@/features/manufacturing/hooks';
import { useAuth } from '@/app/AuthProvider';
import { canWrite } from '@/lib/permissions';
import { formatCurrency, formatDate, formatPercent } from '@/lib/formatters';

function builderName(r: ProductionFormRow): string {
  return r.builder?.full_name || r.builder?.email || '';
}

function facetOptions(values: Array<string | null | undefined>) {
  return Array.from(new Set(values.filter((v): v is string => !!v)))
    .sort((a, b) => a.localeCompare(b))
    .map((v) => ({ value: v, label: v }));
}

/**
 * Production — a running ledger of every production form submitted, one
 * row per form. Submitting a form adds the produced item to on-hand and
 * consumes the components used to build them.
 */
export function ProductionPage() {
  const { role } = useAuth();
  const { data, isLoading } = useProductionForms();
  const [formOpen, setFormOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const canManage = canWrite(role, 'manufacturing');
  const rows = useMemo<ProductionFormRow[]>(() => data ?? [], [data]);
  const selected = rows.find((r) => r.id === selectedId) ?? null;

  // `?form=<id>` opens that form (item movement history links here).
  // Consumed once so a refresh shows the list.
  const [params, setParams] = useSearchParams();
  const requestedFormId = params.get('form');
  useEffect(() => {
    if (!requestedFormId || !data) return;
    if (data.some((r) => r.id === requestedFormId)) setSelectedId(requestedFormId);
    else toast.error('That production form isn’t in the list.');
    const next = new URLSearchParams(params);
    next.delete('form');
    setParams(next, { replace: true });
  }, [requestedFormId, data, params, setParams]);

  const stats = useMemo(() => {
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const recent = rows.filter((r) => new Date(r.produced_at).getTime() >= cutoff);
    const good = recent.reduce((acc, r) => acc + r.quantity, 0);
    const scrap = recent.reduce((acc, r) => acc + r.scrap_quantity, 0);
    return {
      forms: recent.length,
      good,
      scrapRate: good + scrap > 0 ? scrap / (good + scrap) : null,
      cost: recent.reduce((acc, r) => acc + r.component_cost_cents, 0),
    };
  }, [rows]);

  const columns = useMemo<ColumnDef<ProductionFormRow>[]>(
    () => [
      {
        id: 'produced_at',
        accessorKey: 'produced_at',
        header: 'Produced',
        size: 110,
        minSize: 90,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">{formatDate(row.original.produced_at)}</span>
        ),
      },
      {
        id: 'production_number',
        accessorKey: 'production_number',
        header: 'Form #',
        size: 110,
        minSize: 90,
        cell: ({ row }) => (
          <span className="whitespace-nowrap font-mono text-xs">{row.original.production_number}</span>
        ),
      },
      {
        id: 'sku',
        accessorFn: (r) => r.items?.sku ?? '',
        header: 'SKU',
        size: 140,
        minSize: 90,
        filterFn: arrIncludesSome,
        cell: ({ row }) => (
          <Link
            to={`/catalog/items/${row.original.item_id}?tab=manufacturing`}
            onClick={(e) => e.stopPropagation()}
            className="whitespace-nowrap font-mono text-xs text-foreground hover:underline"
          >
            {row.original.items?.sku ?? '—'}
          </Link>
        ),
      },
      {
        id: 'item',
        accessorFn: (r) => r.items?.name ?? '',
        header: 'Item produced',
        size: 220,
        minSize: 140,
        cell: ({ row }) => <span className="font-medium">{row.original.items?.name ?? '—'}</span>,
      },
      {
        id: 'quantity',
        accessorKey: 'quantity',
        header: () => <span className="block text-right">Good</span>,
        size: 90,
        minSize: 70,
        sortingFn: 'basic',
        cell: ({ row }) => (
          <span className="block text-right tabular-nums">
            {row.original.quantity.toLocaleString()}
            <span className="ml-1 text-xs text-muted-foreground">{row.original.items?.base_uom}</span>
          </span>
        ),
      },
      {
        id: 'scrap_quantity',
        accessorKey: 'scrap_quantity',
        header: () => <span className="block text-right">Scrap</span>,
        size: 80,
        minSize: 60,
        sortingFn: 'basic',
        cell: ({ row }) => (
          <span
            className={
              row.original.scrap_quantity > 0
                ? 'block text-right tabular-nums text-fresh-salmon-dark'
                : 'block text-right tabular-nums text-muted-foreground'
            }
          >
            {row.original.scrap_quantity.toLocaleString()}
          </span>
        ),
      },
      {
        id: 'lot',
        accessorFn: (r) => r.lots?.lot_number ?? '',
        header: 'Lot',
        size: 110,
        minSize: 80,
        cell: ({ row }) => (
          <span className="whitespace-nowrap font-mono text-xs text-muted-foreground">
            {row.original.lots?.lot_number ?? '—'}
          </span>
        ),
      },
      {
        id: 'location',
        accessorFn: (r) => r.locations?.name ?? '',
        header: 'Put away to',
        size: 150,
        minSize: 100,
        filterFn: arrIncludesSome,
      },
      {
        id: 'workstation',
        accessorFn: (r) => r.workstations?.name ?? '',
        header: 'Workstation',
        size: 130,
        minSize: 90,
        filterFn: arrIncludesSome,
        cell: ({ row }) => row.original.workstations?.name ?? <span className="text-muted-foreground">—</span>,
      },
      {
        id: 'builder',
        accessorFn: builderName,
        header: 'Built by',
        size: 150,
        minSize: 100,
        filterFn: arrIncludesSome,
      },
      {
        id: 'components',
        accessorFn: (r) => r.production_form_components.length,
        header: () => <span className="block text-right">Parts</span>,
        size: 80,
        minSize: 60,
        sortingFn: 'basic',
        cell: ({ row }) => (
          <span className="block text-right tabular-nums text-muted-foreground">
            {row.original.production_form_components.length}
          </span>
        ),
      },
      {
        id: 'unit_cost',
        accessorFn: (r) => r.unit_cost_cents ?? 0,
        header: () => <span className="block text-right">Unit cost</span>,
        size: 110,
        minSize: 90,
        sortingFn: 'basic',
        cell: ({ row }) => (
          <span className="block text-right tabular-nums">{formatCurrency(row.original.unit_cost_cents)}</span>
        ),
      },
      {
        id: 'build_cost',
        accessorKey: 'component_cost_cents',
        header: () => <span className="block text-right">Build cost</span>,
        size: 120,
        minSize: 90,
        sortingFn: 'basic',
        cell: ({ row }) => (
          <span className="block text-right tabular-nums text-muted-foreground">
            {formatCurrency(row.original.component_cost_cents)}
          </span>
        ),
      },
    ],
    [],
  );

  const facetedFilters: FacetedFilter[] = useMemo(
    () => [
      { columnId: 'sku', title: 'SKU', options: facetOptions(rows.map((r) => r.items?.sku)) },
      { columnId: 'location', title: 'Location', options: facetOptions(rows.map((r) => r.locations?.name)) },
      { columnId: 'workstation', title: 'Workstation', options: facetOptions(rows.map((r) => r.workstations?.name)) },
      { columnId: 'builder', title: 'Built by', options: facetOptions(rows.map(builderName)) },
    ],
    [rows],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Production"
        actions={
          <>
            <Button asChild variant="outline">
              <Link to="/mfg/production/employee-matrix">
                <Grid3x3 className="h-4 w-4" />
                Employee matrix
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/mfg/production/units">
                <ScanBarcode className="h-4 w-4" />
                Unit log
              </Link>
            </Button>
            {canManage && (
              <Button onClick={() => setFormOpen(true)}>
                <Factory className="h-4 w-4" />
                Record production
              </Button>
            )}
          </>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Production forms (30 days)" value={stats.forms.toLocaleString()} loading={isLoading} />
        <MetricCard label="Good units (30 days)" value={stats.good.toLocaleString()} loading={isLoading} />
        <MetricCard
          label="Scrap rate (30 days)"
          value={stats.scrapRate == null ? '—' : formatPercent(stats.scrapRate)}
          loading={isLoading}
        />
        <MetricCard
          label="Build cost (30 days)"
          value={formatCurrency(stats.cost, { whole: true })}
          loading={isLoading}
        />
      </div>

      <Card>
        <CardContent className="pt-6">
          <DataTable<ProductionFormRow, unknown>
            data={rows}
            columns={columns}
            isLoading={isLoading}
            getRowId={(r) => r.id}
            onRowClick={(r) => setSelectedId(r.id)}
            searchPlaceholder="Search by form #, SKU, item, lot, or builder…"
            facetedFilters={facetedFilters}
            initialPageSize={50}
            pageSizes={[25, 50, 100, 200]}
            tableId="manufacturing-production"
            enableColumnResizing
            mobileCard={{
              title: (r) => r.items?.name ?? '—',
              subtitle: (r) => (
                <span className="font-mono text-[11px]">
                  {r.production_number} · {r.items?.sku}
                </span>
              ),
              badges: (r) => [
                r.locations?.name ? (
                  <Badge key="loc" variant="secondary">
                    {r.locations.name}
                  </Badge>
                ) : null,
                r.scrap_quantity > 0 ? (
                  <Badge key="scrap" variant="warning-soft">
                    {r.scrap_quantity.toLocaleString()} scrap
                  </Badge>
                ) : null,
              ],
              amount: (r) => ({
                value: `${r.quantity.toLocaleString()} ${r.items?.base_uom ?? ''}`,
                tone: 'neutral',
                caption: formatCurrency(r.component_cost_cents),
              }),
              meta: (r) => [
                { label: 'Produced', value: formatDate(r.produced_at) },
                { label: 'Built by', value: builderName(r) || '—' },
              ],
            }}
            emptyState={
              <EmptyState
                icon={Factory}
                title="No production recorded yet"
                action={
                  canManage ? (
                    <Button onClick={() => setFormOpen(true)}>
                      <Factory className="h-4 w-4" />
                      Record production
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        </CardContent>
      </Card>

      {canManage && <ProductionFormDialog open={formOpen} onOpenChange={setFormOpen} />}
      <ProductionFormSheet
        form={selected}
        open={selected != null}
        onOpenChange={(open) => !open && setSelectedId(null)}
      />
    </div>
  );
}
